/**
 * API Client
 * Fetch-wrapper voor de backend-API. Vervangt de directe Supabase-anon-key
 * reads in de frontend. Alle data-reads gaan via een Bearer-JWT.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'lm_jwt';

// --- Token helpers ---------------------------------------------------------
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore storage errors */
  }
};

export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore storage errors */
  }
};

/**
 * Decodeer JWT-payload zonder verificatie (alleen voor exp-check client-side).
 * Returns null bij een ongeldig/ontbrekend token.
 */
export const decodeToken = (token = getToken()) => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * True als er een token aanwezig is dat (nog) niet verlopen is.
 */
export const hasValidToken = () => {
  const payload = decodeToken();
  if (!payload) return false;
  if (payload.exp && Date.now() >= payload.exp * 1000) return false;
  return true;
};

/**
 * Leest een token uit de URL (`?jwt=...`) en slaat het op, dan strippen uit de URL.
 * Gebruikt door de PDF-service die het dashboard headless rendert met een report-token.
 * Draait bij module-load zodat het token er is voordat de auth-guards checken.
 */
export const ingestUrlToken = () => {
  if (typeof window === 'undefined' || !window.location) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const jwt = params.get('jwt');
    if (!jwt) return;
    setToken(jwt);
    params.delete('jwt');
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
  } catch {
    /* ignore */
  }
};

ingestUrlToken();

// --- Querystring -----------------------------------------------------------
const buildQuery = (params) => {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    usp.append(key, value);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
};

const joinUrl = (base, path) => `${base}${path.startsWith('/') ? path : `/${path}`}`;

/**
 * Handelt 401 af: token wissen en login opnieuw laten verschijnen.
 */
const handleUnauthorized = () => {
  clearToken();
  // Zacht: herlaad de pagina zodat de auth-guards de login-schermen tonen.
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

// --- Core fetch ------------------------------------------------------------
export const apiGet = async (path, params) => {
  const token = getToken();
  const res = await fetch(joinUrl(API_BASE, path) + buildQuery(params), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(`API ${res.status} ${path}${detail ? `: ${detail}` : ''}`);
  }
  return res.json();
};

export const apiPost = async (path, body) => {
  const token = getToken();
  const res = await fetch(joinUrl(API_BASE, path), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });

  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    const err = new Error(`API ${res.status} ${path}${detail ? `: ${detail}` : ''}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * Raw GET op een absoluut pad (buiten /api), bv. /health.
 */
export const apiGetRaw = async (absolutePath) => {
  const res = await fetch(absolutePath, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`API ${res.status} ${absolutePath}`);
  return res.json();
};

const apiClient = { apiGet, apiPost, apiGetRaw, getToken, setToken, clearToken, hasValidToken, decodeToken };
export default apiClient;
